import { useEffect, useState, useCallback } from 'react';
import { Bug, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, Eye, X, Check, RotateCcw, Server, Monitor } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

/**
 * Runtime greške - isto ono što stiže na Telegram, samo sa evidencijom.
 *
 * Telegram je bio jedini prijemnik i ništa nije čuvao, pa panel nije imao odakle
 * da prikaže spisak. Sada se svaka greška upisuje, grupisana po otisku: jedan
 * red po problemu, sa brojačem pojava - a ne 400 redova iz iste petlje.
 */

type ErrorRow = {
  id: string;
  source: 'server' | 'client';
  app: string | null;
  name: string;
  message: string;
  url: string | null;
  method: string | null;
  statusCode: number | null;
  tenantId: string | null;
  tenantName: string | null;
  env: string | null;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
};

type ErrorDetail = ErrorRow & { stack: string | null; fingerprint: string; userId: string | null; resolvedBy: string | null };

type Stats = { unresolved: number; last24h: number; occurrences24h: number; total: number };

const PAGE_SIZE = 50;

export function ErrorReportsPage() {
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [status, setStatus] = useState<'unresolved' | 'resolved' | 'all'>('unresolved');
  const [source, setSource] = useState('');
  const [days, setDays] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      if (source) params.set('source', source);
      if (days) params.set('days', days);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));

      const [list, s] = await Promise.all([
        api.get<{ rows: ErrorRow[]; total: number }>('/admin/errors?' + params.toString()),
        api.get<Stats>('/admin/errors/stats'),
      ]);
      setRows(list.rows);
      setTotal(list.total);
      setStats(s);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [status, source, days, search, offset]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setOffset(0); }, [status, source, days, search]);

  const setResolved = async (id: string, resolved: boolean) => {
    try {
      await api.post(`/admin/errors/${id}/${resolved ? 'resolve' : 'reopen'}`, {});
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Greške u radu</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Iste one koje idu na Telegram - grupisane po problemu, sa brojem pojava
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
          <StatCard label="Nerešeno" value={stats.unresolved} accent={stats.unresolved > 0 ? '#fca5a5' : undefined} />
          <StatCard label="Aktivno u 24h" value={stats.last24h} />
          <StatCard label="Pojava u 24h" value={stats.occurrences24h} />
          <StatCard label="Ukupno problema" value={stats.total} />
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16, marginBottom: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) 2fr auto', gap: 10, alignItems: 'end' }}>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value as any)} style={input} data-testid="errors-status">
            <option value="unresolved">Nerešene</option>
            <option value="resolved">Rešene</option>
            <option value="all">Sve</option>
          </select>
        </Field>
        <Field label="Poreklo">
          <select value={source} onChange={(e) => setSource(e.target.value)} style={input}>
            <option value="">Sve</option>
            <option value="server">Server</option>
            <option value="client">Aplikacija klijenta</option>
          </select>
        </Field>
        <Field label="Period">
          <select value={days} onChange={(e) => setDays(e.target.value)} style={input}>
            <option value="">Sve vreme</option>
            <option value="1">Poslednja 24h</option>
            <option value="7">Poslednjih 7 dana</option>
            <option value="30">Poslednjih 30 dana</option>
          </select>
        </Field>
        <Field label="Pretraga">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="poruka, tip greške ili ruta"
            style={input}
          />
        </Field>
        <button
          onClick={() => { setStatus('unresolved'); setSource(''); setDays(''); setSearch(''); }}
          style={{ ...btnSecondary, alignSelf: 'end' }}
        >
          Resetuj
        </button>
      </div>

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          {/* `fixed` + sirine: bez toga duga poruka razvuce tabelu i istera
              „Pojava" i dugmad van vidnog polja. */}
          <table style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse', tableLayout: 'fixed' }} data-testid="errors-table">
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={{ ...th, width: 110 }}>Poslednji put</th>
                <th style={{ ...th, width: 90 }}>Poreklo</th>
                <th style={th}>Greška</th>
                <th style={{ ...th, width: 190 }}>Gde</th>
                <th style={{ ...th, width: 110 }}>Klijent</th>
                <th style={{ ...th, width: 70, textAlign: 'right' }}>Pojava</th>
                <th style={{ ...th, width: 84, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Bug size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema grešaka za odabrane filtere</div>
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} style={r.resolvedAt ? { opacity: 0.55 } : undefined}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDate(r.lastSeenAt)}
                  </td>
                  <td style={td}><SourceBadge row={r} /></td>
                  <td style={{ ...td, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-text-strong)', fontSize: 12 }}>{r.name}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.message}
                    </div>
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.url ? `${r.method ? r.method + ' ' : ''}${r.url}` : '-'}
                  </td>
                  <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tenantName || '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span style={countBadge(r.occurrences)}>{r.occurrences}</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setSelectedId(r.id)} style={iconBtn} title="Detalji"><Eye size={13} /></button>
                    {r.resolvedAt
                      ? <button onClick={() => setResolved(r.id, false)} style={iconBtn} title="Vrati na spisak"><RotateCcw size={13} /></button>
                      : <button onClick={() => setResolved(r.id, true)} style={iconBtn} title="Označi kao rešeno"><Check size={13} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div style={{ padding: '12px 14px', borderTop: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text-muted)' }}>
            <div>Prikazano {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} od {total}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0} style={{ ...iconBtn, padding: '6px 10px', opacity: offset === 0 ? 0.4 : 1 }}>
                <ChevronLeft size={13} />
              </button>
              <span>Strana {currentPage} / {totalPages}</span>
              <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total} style={{ ...iconBtn, padding: '6px 10px', opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <ErrorDetailDialog
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onResolvedChange={async (resolved) => { await setResolved(selectedId, resolved); setSelectedId(null); }}
        />
      )}
    </div>
  );
}

function ErrorDetailDialog({ id, onClose, onResolvedChange }: {
  id: string;
  onClose: () => void;
  onResolvedChange: (resolved: boolean) => void;
}) {
  useEscapeToClose(onClose);
  const [row, setRow] = useState<ErrorDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Stack se dovlaci tek ovde - u spisku bi 50 punih stack-ova bilo stotine
    // kilobajta koje niko ne cita.
    api.get<ErrorDetail>(`/admin/errors/${id}`)
      .then(setRow)
      .catch((e: any) => setErr(e?.data?.message || e?.message || 'Greška'));
  }, [id]);

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...dialog, maxWidth: 780 }}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Detalji greške</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflow: 'auto' }}>
          {err && <div style={errorBox}><AlertTriangle size={14} /> {err}</div>}
          {!row && !err && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Učitavanje...</div>}
          {row && (
            <>
              <DetailRow label="Tip" value={row.name} />
              <DetailRow label="Poruka" value={row.message} />
              <DetailRow label="Poreklo" value={row.source === 'client' ? `Aplikacija klijenta${row.app ? ` (${row.app})` : ''}` : 'Server'} />
              {row.url && <DetailRow label="Gde" value={`${row.method ? row.method + ' ' : ''}${row.url}`} mono />}
              {row.statusCode ? <DetailRow label="HTTP status" value={String(row.statusCode)} mono /> : null}
              <DetailRow label="Broj pojava" value={String(row.occurrences)} />
              <DetailRow label="Prvi put" value={new Date(row.firstSeenAt).toLocaleString('sr-RS')} />
              <DetailRow label="Poslednji put" value={new Date(row.lastSeenAt).toLocaleString('sr-RS')} />
              <DetailRow label="Klijent" value={row.tenantName || row.tenantId || '-'} />
              {row.userId && <DetailRow label="Korisnik" value={row.userId} mono />}
              {row.env && <DetailRow label="Okruženje" value={row.env} />}
              {row.stack && (
                <div>
                  <label style={labelStyle}>Stack</label>
                  <pre style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 12, fontSize: 11, color: 'var(--color-text)', overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap' }}>
                    {row.stack}
                  </pre>
                </div>
              )}
              <div>
                <label style={labelStyle}>Otisak (ključ grupisanja)</label>
                <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>{row.fingerprint}</div>
              </div>
            </>
          )}
        </div>
        {row && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {row.resolvedAt
              ? <button onClick={() => onResolvedChange(false)} style={btnSecondary}><RotateCcw size={13} /> Vrati na spisak</button>
              : <button onClick={() => onResolvedChange(true)} style={btnSecondary}><Check size={13} /> Označi kao rešeno</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function SourceBadge({ row }: { row: ErrorRow }) {
  const isClient = row.source === 'client';
  const Icon = isClient ? Monitor : Server;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 600,
      background: isClient ? 'rgba(59,130,246,0.15)' : 'rgba(124,58,237,0.15)',
      color: isClient ? '#93c5fd' : '#c4b5fd',
    }}>
      <Icon size={11} /> {isClient ? (row.app || 'klijent') : 'server'}
    </span>
  );
}

/** Brojka koja bode oci tek kad je velika - inace je samo sum. */
function countBadge(n: number): React.CSSProperties {
  const hot = n >= 50;
  const warm = n >= 10;
  return {
    fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontFamily: 'monospace',
    background: hot ? 'rgba(239,68,68,0.18)' : warm ? 'rgba(245,158,11,0.15)' : 'var(--color-bg)',
    color: hot ? '#fca5a5' : warm ? '#fcd34d' : 'var(--color-text-muted)',
  };
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || 'var(--color-text-strong)', marginTop: 4 }}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ fontSize: 13, color: 'var(--color-text)', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const formatDate = (iso: string) => new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2, fontWeight: 500 };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
